import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { AuthService, TokenService, User } from '@core';

import { ApiKey, ApiKeyService } from '@shared/services/api-key.service';
import { CommonService } from '@shared/services/common.service';
import { ECRService } from '@shared/services/ecr.service';
import { ConfluenceService } from '@shared/services/integrations/confluence.service';
import { VeevaService } from '@shared/services/integrations/veeva.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';
import { parseMetadataValue } from '@shared/utils/parse-metadata-value';

import { VeevaTaxonomySettingsComponent } from './veeva-taxonomy-settings/veeva-taxonomy-settings.component';

@Component({
  selector: 'app-api-access-settings',
  templateUrl: './api-access-settings.component.html',
  styleUrls: ['./api-access-settings.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatButton,
    MatIconButton,
    MatIcon,
    MatChipsModule,
    MatTooltip,
    VeevaTaxonomySettingsComponent,
  ],
})
export class ApiAccessSettingsComponent implements OnInit, OnDestroy {
  @ViewChild(VeevaTaxonomySettingsComponent)
  private veevaTaxonomySettings?: VeevaTaxonomySettingsComponent;

  private _subs: Subscription[] = [];
  protected editECRAccessForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    password: ['', [Validators.required]],
  });
  protected confluenceSettings = this.fb.nonNullable.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
    url: ['', [Validators.required]],
  });

  protected veevaSettings = this.fb.nonNullable.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
    url: ['', [Validators.required]],
  });

  // Chat Widget API Key
  protected currentApiKey: ApiKey | null = null;
  protected newlyGeneratedKey: string | null = null;
  protected isGeneratingKey = false;
  protected isLoadingApiKey = true;
  protected apiKeyForm = this.fb.nonNullable.group({
    allowedDomains: ['', [Validators.required]],
  });

  // Current user
  private _user: User = {};

  constructor(
    private fb: FormBuilder,
    private _commonService: CommonService,
    private _ecrService: ECRService,
    private _metadataService: MetadataService,
    private _confluenceService: ConfluenceService,
    private _veevaService: VeevaService,
    private _apiKeyService: ApiKeyService,
    private _clipboard: Clipboard,
    private _authService: AuthService,
    private _tokenService: TokenService
  ) {}

  ngOnInit(): void {
    this._ecrService.fetchECRCredentials();
    this._subs.push(
      this._ecrService.getECRCredentialsObservable().subscribe(result => {
        this.editECRAccessForm.patchValue({
          name: result?.name,
          password: result?.password,
        });
      })
    );
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.ConfluenceSettings).subscribe({
        next: metadata => {
          const confluenceSettings = parseMetadataValue<{
            username?: string;
            password?: string;
            url?: string;
          }>(metadata.value);
          if (confluenceSettings) {
            this.confluenceSettings.patchValue({
              username: confluenceSettings.username ?? '',
              password: confluenceSettings.password ?? '',
              url: confluenceSettings.url ?? '',
            });
          }
        },
      })
    );

    this._subs.push(
      this._metadataService.get<unknown>(MetadataSlugs.VeevaSettings).subscribe({
        next: metadata => {
          const veevaSettings = parseMetadataValue<{
            username?: string;
            password?: string;
            url?: string;
          }>(metadata.value);
          if (veevaSettings) {
            this.veevaSettings.patchValue({
              username: veevaSettings.username ?? '',
              password: veevaSettings.password ?? '',
              url: veevaSettings.url ?? '',
            });
          }
        },
      })
    );

    // Subscribe to current user
    this._subs.push(
      this._authService.user().subscribe(user => {
        this._user = user;
      })
    );

    // Get user ID from JWT token and fetch their API key
    const userId = this._tokenService.getUserIdFromToken();
    if (userId) {
      this.loadCurrentApiKey(userId);
    } else {
      this.isLoadingApiKey = false;
    }
  }

  private loadCurrentApiKey(userId: string): void {
    this._apiKeyService.fetchApiKeyForUser(userId).subscribe({
      next: apiKey => {
        this.currentApiKey = apiKey;
        if (apiKey?.allowedDomains) {
          this.apiKeyForm.patchValue({
            allowedDomains: apiKey.allowedDomains.join(', '),
          });
        }
        this.isLoadingApiKey = false;
      },
      error: () => {
        this.isLoadingApiKey = false;
      },
    });
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  saveECRAccessSettings(): void {
    const name = this.editECRAccessForm.controls.name.value;
    const password = this.editECRAccessForm.controls.password.value;
    this._ecrService.updateECRCredentials(name, password, () =>
      this._commonService.openSuccessSnackBar('RISE credentials are successfully edited')
    );
  }

  saveVeevaSettings() {
    const form = this.veevaSettings.controls;
    if (this.veevaSettings.invalid) {
      this._commonService.openWarningSnackBar('Please fill all fields');
      return;
    }

    // Trailing slashes break Veeva API path joins (`//api/...` → MALFORMED_URL).
    const url = form.url.value.trim().replace(/\/+$/, '');
    form.url.setValue(url, { emitEvent: false });

    this._veevaService.saveSettings(
      {
        username: form.username.value,
        password: form.password.value,
        url,
      },
      () => {
        this._commonService.openSuccessSnackBar('Veeva settings stored successfully');
        this.veevaTaxonomySettings?.onCredentialsSaved();
      },
      error => {
        this._commonService.openWarningSnackBar(
          error?.error?.error || error?.message || 'Failed to save Veeva settings'
        );
      }
    );
  }

  saveConfluenceSettings() {
    const form = this.confluenceSettings.controls;
    if (this.confluenceSettings.invalid) {
      this._commonService.openWarningSnackBar('Please fill all fields');
      return;
    }
    this._confluenceService.saveConfluenceSettings(
      {
        username: form.username.value,
        password: form.password.value,
        url: form.url.value,
      },
      () => {
        this._commonService.openSuccessSnackBar('Confluence settings stored successfully');
      }
    );
  }

  // Chat Widget API Key Methods
  generateApiKey(): void {
    // Get user ID from JWT token (more reliable than user object)
    const userId = this._tokenService.getUserIdFromToken();
    console.log('[API Key] User ID from token:', userId);
    if (!userId) {
      this._commonService.openWarningSnackBar('User not authenticated');
      return;
    }

    const domainsInput = this.apiKeyForm.controls.allowedDomains.value;
    if (!domainsInput.trim()) {
      this._commonService.openWarningSnackBar('Please enter at least one allowed domain');
      return;
    }

    const allowedDomains = domainsInput
      .split(',')
      .map((d: string) => d.trim())
      .filter((d: string) => d.length > 0);

    if (allowedDomains.length === 0) {
      this._commonService.openWarningSnackBar('Please enter at least one allowed domain');
      return;
    }

    this.isGeneratingKey = true;
    this._apiKeyService.generateApiKey(userId, allowedDomains).subscribe({
      next: response => {
        this.newlyGeneratedKey = response.apiKey;
        this.currentApiKey = {
          keyId: response.keyId,
          allowedDomains: response.allowedDomains,
          createdAt: new Date().toISOString(),
          isActive: true,
        };
        this.isGeneratingKey = false;
        this._commonService.openSuccessSnackBar(
          'API key generated successfully! Copy it now - it won\'t be shown again.'
        );
      },
      error: err => {
        this.isGeneratingKey = false;
        this._commonService.openWarningSnackBar(err.error?.message || 'Failed to generate API key');
      },
    });
  }

  revokeApiKey(): void {
    if (!this.currentApiKey?.keyId) return;

    if (
      !confirm(
        'Are you sure you want to revoke this API key? Any integrations using it will stop working.'
      )
    ) {
      return;
    }

    this._apiKeyService.revokeApiKey(this.currentApiKey.keyId).subscribe({
      next: () => {
        this.currentApiKey = null;
        this.newlyGeneratedKey = null;
        this.apiKeyForm.reset();
        this._commonService.openSuccessSnackBar('API key revoked successfully');
      },
      error: err => {
        this._commonService.openWarningSnackBar(err.error?.message || 'Failed to revoke API key');
      },
    });
  }

  copyApiKey(): void {
    if (this.newlyGeneratedKey) {
      this._clipboard.copy(this.newlyGeneratedKey);
      this._commonService.openSuccessSnackBar('API key copied to clipboard');
    }
  }

  getEmbedCode(): string {
    if (!this.newlyGeneratedKey) return '';
    return `<script>
  window.ONERAY_API_KEY = '${this.newlyGeneratedKey}';
</script>
<script src="https://cdn.oneray.ai/embed.js" async></script>`;
  }

  copyEmbedCode(): void {
    const code = this.getEmbedCode();
    if (code) {
      this._clipboard.copy(code);
      this._commonService.openSuccessSnackBar('Embed code copied to clipboard');
    }
  }
}
